const AbstractCloverTransaction = require('@/lib/PaymentMethods/Clover/AbstractCloverTransaction');
const CloverCreditResponseState = require('@/lib/PaymentMethods/Clover/Enums/CloverCreditResponseState');
const CloverPaymentAndRefundResult = require('@/lib/PaymentMethods/Clover/Enums/CloverPaymentAndRefundResult');
const CloverPaymentIssueState = require('@/lib/PaymentMethods/Clover/Enums/CloverPaymentIssueState');
const CloverTransactionClassName = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionClassName');
const CloverTransactionEndState = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionEndState');
const CloverTransactionState = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionState');
const { fromMinorUnit } = require('@/functions/math');

// Note: To VOID an existing completed transaction use CloverVoidTransaction.
class CloverTransaction extends AbstractCloverTransaction {
    /**
     * CloverTransaction holds all the information about a SALE or REFUND request made to the Clover API.
     * As the transaction is processed by Clover we update this object to store the current state.
     *
     * The Clover API expects all amounts in minor units, and we do all calculations in minor units.
     * The major unit properties are just for convenience.
     *
     * @param {number} amountInMinorUnits
     * @param {string} thirdPartyId
     * @param {string} externalReferenceId
     */
    constructor(amountInMinorUnits, thirdPartyId, externalReferenceId) {
        super(CloverTransactionClassName.PAYMENT);
        if (!amountInMinorUnits || !Number.isInteger(amountInMinorUnits)) {
            throw new Error('CloverTransaction requires amountInMinorUnits to be an Integer');
        }

        if (!thirdPartyId) {
            throw new Error('CloverTransaction requires thirdPartyId');
        }

        if (!externalReferenceId) {
            throw new Error('CloverTransaction requires externalReferenceId');
        }

        /**
         * The total amount in minor units including tips.
         *
         * @type {number}
         */
        this.amountInMinorUnits = amountInMinorUnits;

        /**
         * The original amount excluding any tips in minor units.
         *
         * @type {number}
         */
        this.originalAmountInMinorUnits = amountInMinorUnits;

        this.originalAmountInMajorUnits = fromMinorUnit(amountInMinorUnits);

        this.tipAmountInMinorUnits = 0;

        this.tipAmountInMajorUnits = 0;

        this.cashbackAmountInMinorUnits = 0;

        this.cashbackAmountInMajorUnits = 0;

        this.cardNotPreseent = false;

        /**
         * @type {string|null}
         */
        this.requestId = null;

        /**
         * @type {string|null}
         */
        this.thirdPartyID = thirdPartyId;

        this.deviceOptions = {};

        /**
         * Is the transaction complete?
         *
         * @type {boolean}
         */
        this.isFinished = false;

        this.paymentIssuePartialCheck = null;
        this.paymentIssueDuplicateCheck = null;
        this.paymentIssueSignatureCheck = null;

        /**
         * A more detailed message than status that should be shown to the operator.
         *
         * @type {?string}
         */
        this.userMessage = null;

        /**
         * An ID that can be passed to the merchant's gateway and ultimately appears in settlement records.
         *
         * @type {string}
         */
        this.externalReferenceId = externalReferenceId;
    }

    setCardNotPresent() {
        this.cardNotPreseent = true;
        this.deviceOptions.cardEntryMethods = ['MANUAL'];
    }

    setDisableCashback() {
        this.deviceOptions.disableCashback = true;
    }

    setTipAmount(tipAmountInMinorUnits) {
        if (!Number.isInteger(tipAmountInMinorUnits)) {
            throw new Error('setTipAmount expects input to be an integer');
        }
        this.tipAmountInMinorUnits = tipAmountInMinorUnits;
        this.tipAmountInMajorUnits = fromMinorUnit(tipAmountInMinorUnits);
    }

    setTransactionSuccessful() {
        this.transactionResult.message = CloverTransactionEndState.SUCCESS;
    }

    addResponse(response) {
        if (this.isFinished || this.transactionResult.message === CloverTransactionEndState.SUCCESS) {
            // If we receive a success response ignore any further responses.
            // In the event that we received a SUCCESS response but the user was trying to cancel the SUCCESS response
            // will not be passed to the transaction instead we wait for the response to a VOID request.
            // @see CloverDriver.sendTransaction();
            console.debug('Clover response received when transaction is finished');
            return;
        }

        this.latestResponse = response;

        if (!this.latestResponse) {
            throw new Error('Missing response');
        }

        // Update the userMessage if there is a new userMessage otherwise leave any existing userMessage.
        if (this.latestResponse.data && this.latestResponse.data.message) {
            this.userMessage = this.latestResponse.data.message;
        }

        // Check the response if it isn't 200 then update messages but don't continue.
        if (!this.isHttpResponseSuccess()) {
            return;
        }

        // The final state of the transaction.
        if (this.latestResponse.data) {
            if (this.latestResponse.data.payment) {
                if (this.latestResponse.data.payment.cashbackAmount) {
                    this.cashbackAmountInMinorUnits = this.latestResponse.data.payment.cashbackAmount;
                    this.cashbackAmountInMajorUnits = fromMinorUnit(this.cashbackAmountInMinorUnits);
                }

                switch (this.latestResponse.data.payment.result) {
                    case CloverPaymentAndRefundResult.SUCCESS:
                        this.handleSuccessResult();
                        break;
                    case CloverPaymentAndRefundResult.VOIDED:
                        this.setTransactionVoided();
                        break;
                    case CloverPaymentAndRefundResult.VOID_FAILED:
                        this.setTransactionSuccessfulVoidFailed();
                        break;
                }
            }

            if (this.latestResponse.data.credit) {
                if (this.latestResponse.data.credit.cardTransaction.state === CloverCreditResponseState.CLOSED) {
                    this.setTransactionSuccessful();
                }
            }
        }
    }

    handleSuccessResult() {
        if (this.latestResponse.data.issues) {
            // There are 4 possible issue states:
            // - duplicate
            // - offline
            // - partialPayment
            // - signature

            // We can ignore offline because we are doing the cloud integration which is by definition online.
            // If either ProPoint or the terminal is offline they can't communicate with each other at all.

            // We need to handle partial payments. A transaction request may return a partial payment when the
            // customer’s card has insufficient funds to complete the transaction.
            // If there is partialPaymentIssue we can never progress past this point.
            // @link https://docs.clover.com/docs/handling-partial-payment-issues
            if (this.latestResponse.data.issues.partialPayment) {
                if (!this.paymentIssuePartialCheck) {
                    // We will just void the transaction automatically.
                    this.paymentIssuePartialCheck = CloverPaymentIssueState.REQUIRED;
                    this.state = CloverTransactionState.CHECKING_PARTIAL_PAYMENT;
                }
                return;
            }

            // We need to handle signatures.
            if (this.latestResponse.data.issues.signature && !this.paymentIssueSignatureCheck) {
                // The operator needs to accept or decline (and then void the transaction)
                this.paymentIssueSignatureCheck = CloverPaymentIssueState.REQUIRED;
                this.state = CloverTransactionState.CHECKING_SIGNATURE;
                return;
            }

            // We need to handle duplicates.
            // To help ensure a merchant does not accidentally charge a customer twice for the same order, your
            // application must check each payment response issues for the duplicate object. When a card with
            // the last 4 digits is used for more than one payment in an hour, POTENTIAL_DUPLICATE is returned.
            // @link https://docs.clover.com/docs/checking-for-duplicate-payments
            if (this.latestResponse.data.issues.duplicate && !this.paymentIssueDuplicateCheck) {
                // The operator needs to accept or decline (and then void the transaction)
                this.paymentIssueDuplicateCheck = CloverPaymentIssueState.REQUIRED;
                this.state = CloverTransactionState.CHECKING_DUPLICATE_CARD;
                return;
            }
        }
        this.setTransactionSuccessful();
    }
}

module.exports = CloverTransaction;
